import { ActionContext } from 'vuex';
import { Geolocation } from '@capacitor/geolocation';
import { Address, Coordinate, CustomerAddress } from '@/models/addressModels';
import {
    addAccountAddressPost,
    editAccountAddressPost,
    addCreditCard,
    deleteAddress,
    deleteStoredCard,
    fetchAccountInfo,
    fetchStoredCards,
    updateStoredCard,
    addRewardToBasket,
    getRewardBasketItems,
    removeItemsFromBasket,
} from '@/services/accountService';
import { deleteLocalStorage, getLocalStorage, localStorageEnum, replaceLocalStorage } from '@/services/localStorageService';
import { DEFAULT_COORDINATES, rewardTypes } from '@/constants';
import useAuth from '@/services/authService';
import useVault from '@/services/vaultService';
import router from '@/router';
import { CheckoutRequest } from '@/models/checkoutModels';
import { validateDeliveryAddress } from '@/services/locationService';
import { Account } from '@/models/account/authModels';
import { alertController } from '@ionic/vue';
import { CreditCard } from '@/models/PaymentModels';
import { errorToast } from '@/services/pageService';
import { HistoryOrder, OrderLogistics } from '@/models/cartModels';
import { orderHistory } from '@/services/orderService';
import { syncUserToBraze } from '@/services/braze/brazeSync';
import { fetchRewardsInfo } from '@/services/accountService';
import { BasketItemsAttributes, BasketItem, Reward, RewardsInfo } from '@/models/account/rewardModels';
import { RewardsInfoDTO, DiscountBasketItemDTO, RewardDTO } from '@/models/account/rewardDTOModels';
import { OrderState } from './orderModule';

const { isAuthenticated, getCognitoUserInfo, logout, isAccessTokenExpired, isRefreshTokenAvailable, refreshSession, clearStorage } = useAuth();
const { initializeUnlockMode } = useVault();

export interface AccountState {
    loginProcessing: boolean;
    loyaltyProcessing: boolean;
    loyaltyInfoProcessing: boolean;
    createAccountProcessing: boolean;
    isOrderHistoryLoading: boolean;
    account?: Account;
    orderHistory?: HistoryOrder[];
    guest: Account;
    userLocation?: Coordinate;
    isAuthenticated: boolean;
    isGeoLocationOn: boolean;
    appInfo?: any;
    rewardsInfo?: RewardsInfo;
    rewardsBasketItems: ReadonlyArray<BasketItem>;
    isRewardsLocked: boolean;
    addingRewardToBasket: boolean;
}

const AccountState: AccountState = {
    loginProcessing: false,
    loyaltyProcessing: false,
    loyaltyInfoProcessing: true,
    createAccountProcessing: false,
    isOrderHistoryLoading: false,
    isAuthenticated: false,
    isGeoLocationOn: false,
    guest: new Account(),
    rewardsBasketItems: [],
    isRewardsLocked: false,
    addingRewardToBasket: false,
};

const initUserLogin = async (context) => {
    context.commit('SET_LOGIN_PROCESSING', true);
    try {
        const user = await getCognitoUserInfo();
        if (user) {
            const accountInfoResponse = await fetchAccountInfo(1);
            if (accountInfoResponse) {
                context.commit('SET_ACCOUNT_INFO', accountInfoResponse);
                const cart = getLocalStorage(localStorageEnum.cart);
                if (cart && cart.lineItems) {
                    context.dispatch('initCart', cart.lineItems);
                }
                context.dispatch('fetchOrderHistory');

                // @ts-ignore
                const orderLogistics = new OrderLogistics(accountInfoResponse.cart.orderDetails?.orderLogistics);

                const previousSelectedLocation = context.getters.getSelectedLocation;
                if (previousSelectedLocation) {
                    orderLogistics.storeNumber = previousSelectedLocation.id;
                }
                context.dispatch('updateEstimatedTime', accountInfoResponse.cart.orderHeader?.submitResult.promiseTime.date);
                context.dispatch('initStoreLocation', orderLogistics);
                context.dispatch('setSavedCards').catch(() => false);

                // Sync user to Braze after successful login
                try {
                    await syncUserToBraze(accountInfoResponse);
                } catch (brazeErr) {
                    // Silently fail - Braze sync is non-critical
                }
            }
        }
    } catch (err: any) {
        errorToast(err);
    } finally {
        context.commit('SET_LOGIN_PROCESSING', false);
    }
};

export default {
    state: AccountState,
    mutations: {
        SET_LOGIN_PROCESSING: (state: AccountState, isProcessing: boolean): void => {
            state.loginProcessing = isProcessing;
        },
        SET_LOGIN_REDIRECT_URL: (state: AccountState, url: string): void => {
            replaceLocalStorage(localStorageEnum.loginRedirectUrl, url, 1);
        },
        RESET_LOGIN_REDIRECT_URL: (): void => {
            replaceLocalStorage(localStorageEnum.loginRedirectUrl, '/', 1);
        },
        SET_LOYALTY_PROCESSING: (state: AccountState, isProcessing: boolean): void => {
            state.loyaltyProcessing = isProcessing;
        },
        SET_LOYALTY_INFO_PROCESSING: (state: AccountState, isProcessing: boolean): void => {
            state.loyaltyInfoProcessing = isProcessing;
        },
        CLEAR_ACCOUNT_INFO: (state: AccountState): void => {
            state.account = undefined;
            state.orderHistory = undefined;
            state.isAuthenticated = false;
            deleteLocalStorage(localStorageEnum.customerName); // Dynatrace Data
            deleteLocalStorage(localStorageEnum.customerPhone); // Dynatrace Data
        },
        SET_ACCOUNT_INFO: (state: AccountState, info: Account): void => {
            state.account = info;
            state.isAuthenticated = !!info;
            replaceLocalStorage(localStorageEnum.customerName, info?.name, 0.04); // Dynatrace Data
            replaceLocalStorage(localStorageEnum.customerPhone, info?.phoneNumber, 0.04); // Dynatrace Data
        },
        SET_ORDER_HISTORY_LOADING: (state: AccountState, isLoading: boolean): void => {
            state.isOrderHistoryLoading = isLoading;
        },
        SET_ORDER_HISTORY: (state: AccountState, history: any): void => {
            const orderHistory: Array<HistoryOrder> = history.map((o) => new HistoryOrder(o));
            state.orderHistory = orderHistory;
        },
        SET_REWARDS_ACCOUNT_INFO: (state: AccountState, info: RewardsInfoDTO): void => {
            const rewards: ReadonlyArray<Reward> = new Array<RewardDTO>(...info.rewards)
                .sort((a, b) => new Date(a.expiringAt).getTime() - new Date(b.expiringAt).getTime())
                .map((r) => {
                    return {
                        id: r.rewardId,
                        name: r.name,
                        description: r.description,
                        imageUrl: r.rewardImageUrl,
                        discountAmount: r.discountAmount,
                        expirationDate: r.expiringAt,
                        type: rewardTypes.REWARD,
                        points: 0,
                    };
                });
            const redeemables: ReadonlyArray<Reward> = info.redeemables
                ?.filter((r) => r.applicableAsLoyaltyRedemption)
                .sort((a, b) => a.points - b.points)
                .map((r) => {
                    return {
                        id: r.redeemableId,
                        name: r.name,
                        description: r.description,
                        imageUrl: r.image,
                        discountAmount: r.discountAmount,
                        expirationDate: r.expiringAt,
                        type: rewardTypes.REDEEMABLE,
                        points: r.points,
                    };
                });
            let nextPoints = 0;
            if (redeemables.length > 0) {
                const redeemablesReverseOrder = new Array(...redeemables).reverse();
                redeemablesReverseOrder.forEach((redeemable: Reward) => {
                    if (redeemable.points > info.accountBalance.redeemablePoints) {
                        nextPoints = redeemable.points;
                    }
                });
            }
            const rewardsInfo: RewardsInfo = {
                rewards: rewards,
                redeemables: redeemables,
                badges: info.challengeBadges.map((b) => {
                    return {
                        id: b.badgeId,
                        challengeCampaignId: b.challengeCampaignId,
                    };
                }),
                accountBalance: {
                    totalPoints: info.accountBalance.redeemablePoints,
                },
                nextRewardThresholdPoints: nextPoints,
            };
            state.rewardsInfo = rewardsInfo;
        },
        SET_BASKET_REWARD_ITEMS: (state: AccountState, items: ReadonlyArray<DiscountBasketItemDTO>): void => {
            const basketItems = items.map((item) => {
                return {
                    basketItemId: item.discountBasketItemId,
                    discountType: item.discountType,
                    discountId: item.discountId,
                    name: item.discountDetails.name,
                    description: item.discountDetails.description,
                    points: item.discountDetails.points,
                    image: item.discountDetails.image,
                };
            });
            state.rewardsBasketItems = basketItems;
        },
        SET_REWARDS_LOCKED: (state: AccountState, isLocked: boolean): void => {
            state.isRewardsLocked = isLocked;
        },
        SET_ADDING_REWARD_TO_BASKET: (state: AccountState, isAdding: boolean): void => {
            state.addingRewardToBasket = isAdding;
        },
        SET_USER_LOCATION: (state: AccountState, location: Coordinate) => {
            state.userLocation = new Coordinate(location);
        },
        ADD_ACCOUNT_ADDRESS: (state: AccountState, location: CustomerAddress) => state.account?.deliveryAddresses.push(location),
        EDIT_ACCOUNT_ADDRESS: (state: AccountState, location: CustomerAddress) => {
            const editedIndex = state.account?.deliveryAddresses.findIndex((address: CustomerAddress) => address.addressId == location.addressId);
            //@ts-ignore
            state.account?.deliveryAddresses.splice(editedIndex, 1, location);
        },
        DELETE_ACCOUNT_ADDRESS: (state: AccountState, location: CustomerAddress) => {
            const removeIndex = state.account?.deliveryAddresses?.findIndex((address) => address == location);
            if (removeIndex != undefined) state.account?.deliveryAddresses.splice(removeIndex, 1);
        },
        ADD_TEMP_ACCOUNT_DETAIL: (state: AccountState, details: CheckoutRequest): void => {
            if (state.account) {
                state.account.tempFirstName = details.firstName;
                state.account.tempLastName = details.lastName;
                state.account.tempPhone = details.phoneNumber;
                state.account.tempEmail = details.email;
                state.account.isMarketingEmail = details.isMarketingEmail;
            } else {
                state.guest.firstName = details.firstName;
                state.guest.lastName = details.lastName;
                state.guest.tempPhone = details.phoneNumber;
                state.guest.tempEmail = details.email;
                state.guest.isMarketingEmail = details.isMarketingEmail;
            }
            replaceLocalStorage(localStorageEnum.customerName, `${details?.firstName} ${details?.lastName}`, 0.4); // Dynatrace Data
            replaceLocalStorage(localStorageEnum.customerPhone, details?.phoneNumber, 0.4); // Dynatrace Data
        },
        ADD_SAVED_CARD: (state: AccountState, card: CreditCard) => state.account?.savedCards.push(card),
        SET_SAVED_CARDS: (state: AccountState, cards: CreditCard[]) => {
            if (state.account) {
                state.account.savedCards = cards;
            }
        },
        UPDATE_SAVED_CARD: (state: AccountState, card: CreditCard) => {
            if (state.account) {
                const cardIndex = state.account.savedCards.findIndex((c: CreditCard) => c.creditCardId == card.creditCardId);
                state.account.savedCards[cardIndex] = card;
            }
        },
        DELETE_SAVED_CARD: (state: AccountState, card: CreditCard) => {
            const { savedCards } = state.account ?? {};
            const creditCardId = card?.creditCardId;
            if (savedCards && creditCardId) {
                const removeIndex = savedCards.findIndex((c) => c.creditCardId === creditCardId);
                if (removeIndex !== -1) {
                    savedCards.splice(removeIndex, 1);
                }
            }
        },
        UPDATE_GEOLOCATION_STATUS: (state: AccountState, isOn: boolean) => {
            state.isGeoLocationOn = isOn;
        },
        SET_APP_INFO: (state: AccountState, info: any) => {
            state.appInfo = info;
        },
    },
    actions: {
        /**
         * Loads logged-in user account data from api or guest data if stored local storage.
         * This cascades into loading selected location, order details, cart, and menu if applicable.
         * - If auth token is expired, this is where unlock mode for native device is set to use biometric login
         */
        async initUserData(context: ActionContext<AccountState, any>): Promise<void> {
            context.dispatch('setGeoLocation'); // ask for users geoLocation;
            await initializeUnlockMode(); // set the identity vaults unlock mode;
            const isAuth = await isAuthenticated(); // check if authed, will use refresh token if available
            const hasExpiredAccessToken = await isAccessTokenExpired();
            if (hasExpiredAccessToken) {
                if (await isRefreshTokenAvailable()) {
                    await refreshSession();
                } else {
                    await clearStorage();
                }
            } else if (isAuth) {
                await initUserLogin(context);
            } else {
                const cart = getLocalStorage(localStorageEnum.cart);
                if (cart && cart?.lineItems) {
                    context.dispatch('initCart', cart?.lineItems);
                }
                let orderDetail: OrderLogistics = getLocalStorage(localStorageEnum.orderLogistics);
                if (!orderDetail) {
                    // If no orderDetail exists in localStorage, add it.
                    replaceLocalStorage(localStorageEnum.orderLogistics, context.getters.getOrderLogistics, 0.1);
                    orderDetail = getLocalStorage(localStorageEnum.orderLogistics);
                }
                context.dispatch('initStoreLocation', orderDetail ? new OrderLogistics(orderDetail) : undefined);
            }
        },
        async setRewardsAccount(context: ActionContext<AccountState & OrderState, any>): Promise<void> {
            context.commit('SET_LOYALTY_INFO_PROCESSING', true);
            try {
                const rewardsInfo: RewardsInfo = await fetchRewardsInfo();
                if (rewardsInfo) {
                    context.commit('SET_REWARDS_ACCOUNT_INFO', rewardsInfo);
                }
            } catch (error: any) {
                errorToast('Unable to load rewards information at this time. Please try again later.');
            } finally {
                context.commit('SET_LOYALTY_INFO_PROCESSING', false);
            }
        },
        async setBasketRewardItems(context: ActionContext<AccountState, any>, validate = false): Promise<void> {
            try {
                const basketItemsResponse = await getRewardBasketItems();
                context.commit('SET_REWARDS_LOCKED', false);
                context.commit('SET_BASKET_REWARD_ITEMS', basketItemsResponse.discountBasketItems);
                if (validate && basketItemsResponse.discountBasketItems.length > 0) {
                    context.dispatch('validateBasketItems');
                } else {
                    context.commit('SET_REWARDS_VALIDATING', false);
                }
            } catch (error: any) {
                if (error?.includes('locked_basket')) {
                    context.commit('SET_REWARDS_LOCKED', true);
                } else {
                    errorToast('Unable to retrieve reward items at this time.');
                }
                context.commit('SET_REWARDS_VALIDATING', false);
            }
        },
        async addRewardToBasket(context: ActionContext<AccountState, any>, reward: Reward): Promise<void> {
            const basketRewardPayload: BasketItemsAttributes = { discountId: reward.id, discountType: reward.type };
            context.commit('SET_ADDING_REWARD_TO_BASKET', true);
            try {
                const basketResponse = await addRewardToBasket(basketRewardPayload);
                context.commit('SET_BASKET_REWARD_ITEMS', basketResponse.discountBasketItems);
            } catch (error: any) {
                if (error.includes('Points Balance is insufficent to process request.')) {
                    errorToast('Insufficient points to add this reward. Earn more points or remove another reward.');
                } else {
                    errorToast('Unable to add reward at this time. Please try again later.');
                }
            } finally {
                context.commit('SET_ADDING_REWARD_TO_BASKET', false);
            }
        },
        async removeRewardFromBasket(context: ActionContext<AccountState, any>, rewardId: number): Promise<void> {
            const basketItems = context.state.rewardsBasketItems ?? [];
            const matchingItem = basketItems.find((item) => item.discountId == rewardId);
            if (matchingItem) {
                try {
                    const removeResponse = await removeItemsFromBasket([matchingItem.basketItemId]);
                    context.commit('SET_BASKET_REWARD_ITEMS', removeResponse.discountBasketItems);
                } catch (error: any) {
                    errorToast('Unable to remove reward at this time. Please try again later.');
                }
            } else {
                errorToast('Reward item not found in basket.');
            }
        },
        setGeoLocation(context: ActionContext<AccountState, any>): void {
            Geolocation.getCurrentPosition({ timeout: 30000 })
                .then((position) => {
                    context.commit('SET_USER_LOCATION', position.coords);
                    context.commit('UPDATE_GEOLOCATION_STATUS', true);
                })
                .catch(() => {
                    context.commit('UPDATE_GEOLOCATION_STATUS', false);
                });
        },
        setLogin(context: ActionContext<AccountState, any>) {
            if (context.getters.isLoginProcessing || context.getters.loggedIn) return;
            context.commit('SET_LOGIN_PROCESSING', true);
            initUserLogin(context);
        },
        setLoginRedirectUrl(context: ActionContext<AccountState, any>, url = '/') {
            context.commit('SET_LOGIN_REDIRECT_URL', url);
        },
        async setLogout(context: ActionContext<AccountState, any>, redirectUrl = '/location'): Promise<void> {
            await clearStorage();
            deleteLocalStorage(localStorageEnum.orderTip);
            context.commit('CLEAR_ACCOUNT_INFO');
            context.dispatch('logoutCart');
            logout()
                .then(() => router.push(redirectUrl))
                .catch(() => clearStorage());
        },
        addUserAddress(context: ActionContext<AccountState, any>, request: CustomerAddress): Promise<Address> {
            request.customerId = context.state.account?.customerId ?? '';
            return new Promise((resolve, reject) => {
                validateDeliveryAddress(new Address(request))
                    .then(async (valid) => {
                        request.storeNumber = valid.storeNumber;
                        if (valid.message.includes('address is corrected.')) {
                            const address1 = valid.address.address1;
                            const address2 = valid.address.address2 ?? '';
                            const city = valid.address.city ?? '';
                            const state = valid.address.state ?? '';
                            const zipCode = valid.address.zip ?? valid.address.zipcode ?? '';

                            const alert = await alertController.create({
                                header: `Address Suggestion`,
                                message: `${address1} ${address2}<br>${city}, ${state} ${zipCode}`,
                                backdropDismiss: true,
                                cssClass: 'valid-address-alert',
                                buttons: [
                                    {
                                        text: 'Check Address',
                                        role: 'cancel',
                                        handler: () => reject('Please validate your delivery address.'),
                                    },
                                    {
                                        text: 'Use This Address',
                                        role: 'confrim',
                                        handler: () => {
                                            //@ts-ignore // set validated/corrected address fields
                                            request.zip = valid.address.zipcode ?? valid.address.zipCode ?? valid.address.zip;
                                            request.address1 = valid.address.address1;
                                            request.city = valid.address.city;
                                            request.state = valid.address.state;
                                            addAccountAddressPost(request)
                                                .then((res) => {
                                                    request.addressId = res.addressId;
                                                    context.commit('ADD_ACCOUNT_ADDRESS', request);
                                                    context.dispatch('foundDeliveryLocation', valid);
                                                    resolve(valid.address);
                                                })
                                                .catch((err) => reject(err));
                                        },
                                    },
                                ],
                            });
                            return alert.present();
                        } else {
                            addAccountAddressPost(request)
                                .then((res) => {
                                    //@ts-ignore // set validated/corrected address fields
                                    request.zip = valid.address.zipcode ?? valid.address.zipCode ?? valid.address.zip;
                                    request.address1 = valid.address.address1;
                                    request.city = valid.address.city;
                                    request.state = valid.address.state;
                                    request.addressId = res.addressId;
                                    context.commit('ADD_ACCOUNT_ADDRESS', request);
                                    context.dispatch('foundDeliveryLocation', valid);
                                    resolve(valid.address);
                                })
                                .catch((err) => reject(err));
                        }
                    })
                    .catch((err) => reject(err));
            });
        },
        editUserAddress(context: ActionContext<AccountState, any>, request: CustomerAddress): Promise<Address> {
            request.customerId = context.state.account?.customerId ?? '';
            return new Promise((resolve, reject) => {
                validateDeliveryAddress(new Address(request))
                    .then(async (valid) => {
                        request.storeNumber = valid.storeNumber;
                        if (valid.message.includes('address is corrected.')) {
                            const address1 = valid.address.address1;
                            const address2 = valid.address.address2 ?? '';
                            const city = valid.address.city ?? '';
                            const state = valid.address.state ?? '';
                            const zipCode = valid.address.zip ?? valid.address.zipcode ?? '';

                            const alert = await alertController.create({
                                header: `Address Suggestion`,
                                message: `${address1} ${address2}<br>${city}, ${state} ${zipCode}`,
                                backdropDismiss: true,
                                cssClass: 'valid-address-alert',
                                buttons: [
                                    {
                                        text: 'Check Address',
                                        role: 'cancel',
                                        handler: () => reject('Please validate your delivery address.'),
                                    },
                                    {
                                        text: 'Use This Address',
                                        role: 'confrim',
                                        handler: () => {
                                            //@ts-ignore // set validated/corrected address fields
                                            request.zip = valid.address.zipcode ?? valid.address.zipCode ?? valid.address.zip;
                                            request.address1 = valid.address.address1;
                                            request.city = valid.address.city;
                                            request.state = valid.address.state;
                                            editAccountAddressPost(request)
                                                .then(() => {
                                                    context.commit('EDIT_ACCOUNT_ADDRESS', request);
                                                    context.dispatch('foundDeliveryLocation', valid);
                                                    resolve(valid.address);
                                                })
                                                .catch((err) => reject(err));
                                        },
                                    },
                                ],
                            });
                            return alert.present();
                        } else {
                            editAccountAddressPost(request)
                                .then(() => {
                                    context.commit('EDIT_ACCOUNT_ADDRESS', request);
                                    context.dispatch('foundDeliveryLocation', valid);
                                    resolve(valid.address);
                                })
                                .catch((err) => reject(err));
                        }
                    })
                    .catch((err) => reject(err));
            });
        },
        deleteUserAddress(context: ActionContext<AccountState, any>, request: CustomerAddress): Promise<any> {
            return new Promise((resolve, reject) => {
                deleteAddress(request.asDelete)
                    .then(() => resolve(context.commit('DELETE_ACCOUNT_ADDRESS', request)))
                    .catch((err) => reject(err));
            });
        },
        setTempAccountDetails(context: ActionContext<AccountState, any>, details: CheckoutRequest): Promise<any> {
            context.commit('ADD_TEMP_ACCOUNT_DETAIL', details);
            return Promise.resolve();
        },
        addSavedCard(context: ActionContext<AccountState, any>, card: CreditCard): Promise<CreditCard | void> {
            return addCreditCard(card)
                .then((res) => {
                    card.creditCardId = res.creditCardID;
                    context.commit('ADD_SAVED_CARD', card);
                    Promise.resolve(card);
                })
                .catch((error) => Promise.reject(error));
        },
        setSavedCards(context: ActionContext<AccountState, any>): Promise<CreditCard[] | void> {
            return fetchStoredCards(context.state.account?.customerId ?? '')
                .then((card) => {
                    context.commit('SET_SAVED_CARDS', card);
                    Promise.resolve(card);
                })
                .catch((error) => Promise.reject(error));
        },
        updateSavedCard(context: ActionContext<AccountState, any>, card: CreditCard): Promise<CreditCard | void> {
            return updateStoredCard(card)
                .then(() => {
                    context.commit('UPDATE_SAVED_CARD', card);
                })
                .catch((error) => Promise.reject(error));
        },
        deleteSavedCard(context: ActionContext<AccountState, any>, card: CreditCard): Promise<CreditCard | void> {
            return deleteStoredCard(card.asDelete())
                .then(() => {
                    context.commit('DELETE_SAVED_CARD', card);
                })
                .catch((error) => Promise.reject(error));
        },
        refreshAccountInfo(context: ActionContext<AccountState, any>) {
            return new Promise((resolve, reject) => {
                fetchAccountInfo(1)
                    .then((res) => resolve(context.commit('SET_ACCOUNT_INFO', res)))
                    .catch((error) => reject(error));
            });
        },
        async fetchOrderHistory(context: ActionContext<AccountState, any>) {
            context.commit('SET_ORDER_HISTORY_LOADING', true);
            if (!context.state.account) return;
            try {
                const response = await orderHistory(context.state.account?.customerId);
                if (response) context.commit('SET_ORDER_HISTORY', response);
            } catch (error) {
                errorToast('Unable to load order history at this time. Please try again later.');
            } finally {
                context.commit('SET_ORDER_HISTORY_LOADING', false);
            }
        },
        setAppInfo(context: ActionContext<AccountState, any>, info) {
            context.commit('SET_APP_INFO', info);
        },
    },
    getters: {
        isLoginProcessing: (state: AccountState): boolean => state.loginProcessing,
        isLoyaltyProcessing: (state: AccountState): boolean => state.loyaltyProcessing,
        isLoyaltyInfoProcessing: (state: AccountState): boolean => state.loyaltyInfoProcessing,
        getIsRewardsLocked: (state: AccountState): boolean => state.isRewardsLocked,
        getAddingRewardToBasket: (state: AccountState): boolean => state.addingRewardToBasket,
        isLoggedIn: (state: AccountState): boolean => state.isAuthenticated,
        isCreateAccountProcessing: (state: AccountState): boolean => state.createAccountProcessing,
        userCoordinates: (state: AccountState): Coordinate => state.userLocation ?? DEFAULT_COORDINATES,
        getAccount: (state: AccountState): Account | undefined => state.account,
        getRewardsInfo: (state: AccountState): RewardsInfo | undefined => state.rewardsInfo,
        getRewardsBasketItems: (state: AccountState): ReadonlyArray<BasketItem> => state.rewardsBasketItems,
        isOrderHistoryLoading: (state: AccountState): boolean => state.isOrderHistoryLoading,
        getOrderHistory: (state: AccountState): HistoryOrder[] | undefined => state.orderHistory,
        getHasOrderHistory: (state: AccountState): boolean => !!state.orderHistory && state.orderHistory.length > 0,
        getOrderHistoryCount: (state: AccountState): number => (state.orderHistory ? state.orderHistory.length : 0),
        getAccountOrGuest: (state: AccountState): Account => state.account ?? state.guest,
        getAccountAddresses: (state: AccountState): CustomerAddress[] => state.account?.deliveryAddresses ?? [],
        getAccountFirstName: (state: AccountState): string | undefined => state.account?.firstName,
        getAccountId: (state: AccountState): string | undefined => state.account?.customerId,
        getAccountSavedCards: (state: AccountState): CreditCard[] => state.account?.savedCards ?? [],
        getAccountDefaultCard: (state: AccountState): CreditCard | undefined => state.account?.savedCards.find((card) => card.isDefaultCard) ?? undefined,
        isGeoLocationOn: (state: AccountState): boolean => state.isGeoLocationOn,
        getLoginRedirectUrl: (): string => getLocalStorage(localStorageEnum.loginRedirectUrl) ?? '/',
        getAppInfo: (state: AccountState): any => state.appInfo,
    },
};
